import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { AvatarUploadModalComponent } from 'src/app/routes/avatar-modal/avatar-upload-modal.component';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'system-config',
  templateUrl: './system-config.component.html',
  styleUrls: ['./system-config.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzTabsModule,
    NzSpinModule,
    NzDividerModule,
    NzTagModule,
    NzDescriptionsModule,
    NzGridModule,
    NzToolTipModule,
    NzModalModule,
    NzSwitchModule,
    NzUploadModule,
    NzAvatarModule,
    NzEmptyModule,
    NzDatePickerModule,
    NzInputNumberModule,
    AvatarUploadModalComponent,
    I18nPipe,
    NzToolTipModule,
    TestIdDirective
  ]
})
export class SystemConfigComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly imageService = inject(ImageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  systemConfig: any = {};
  configForm: FormGroup = this.fb.group({});
  configLoading = false;
  configSaving = false;
  groupedConfigParams: any[] = [];

  // 头像上传相关属性
  // 在类属性中添加新属性
  avatarUploadLoading = false;
  currentAvatarField = '';
  isAvatarModalVisible = false;
  avatarUrls: { [key: string]: string } = {}; // 存储各个头像字段的URL
  tempAvatarFiles: { [key: string]: File } = {}; // 存储各个头像字段的临时文件
  avatarFileIds: { [key: string]: string } = {}; // 存储已上传的头像fileId
  loadingAvatars: Set<string> = new Set(); // 跟踪正在加载的头像字段

  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    if (!this.currentAvatarField) return;

    // 创建临时文件
    const tempFile = new File([event.blob], 'avatar.png', { type: 'image/png' });

    // 立即显示临时文件，无需等待上传完成
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.avatarUrls[this.currentAvatarField] = e.target.result;
      // 强制触发变更检测，立即显示图片
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(tempFile);

    // 关闭模态框
    this.isAvatarModalVisible = false;
    const currentField = this.currentAvatarField;
    this.currentAvatarField = '';

    // 设置上传状态
    this.avatarUploadLoading = true;

    const args = { x: 0, y: 0, size: event.width, useMultiSize: true };
    this.http.postFormData(ConstantUtil.BASE_ATTACHMENT_V1_AVATAR_UPLOAD, args, tempFile).subscribe({
      next: res => {
        this.avatarUploadLoading = false;
        if (res.success && res.data && res.data.fileId) {
          // 保存fileId
          this.avatarFileIds[currentField] = res.data.fileId;

          // 更新表单控件值
          const control = this.configForm.get(currentField);
          if (control) {
            control.setValue(res.data.fileId);
            control.markAsDirty();
          }

          this.message.success(this.i18n.fanyi('system-config.avatar.upload-success'));
        } else {
          // 上传失败时，清除临时显示的图片
          delete this.avatarUrls[currentField];
          this.cdr.detectChanges();
          this.message.error(`${this.i18n.fanyi('system-config.avatar.upload-failed')}: ${res.msg ?? ''}`);
        }
      },
      error: () => {
        this.avatarUploadLoading = false;
        // 上传失败时，清除临时显示的图片
        delete this.avatarUrls[currentField];
        this.cdr.detectChanges();
        this.message.error(this.i18n.fanyi('system-config.avatar.upload-failed'));
      }
    });

    this.handleAvatarModalCancel();
  }

  // 简化saveConfig方法
  async saveConfig(): Promise<void> {
    if (this.configForm.invalid) {
      Object.values(this.configForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.message.warning(this.i18n.fanyi('system-config.form.required-fields'));
      return;
    }

    this.configSaving = true;

    try {
      // 准备提交的数据
      const formValues = this.configForm.getRawValue();
      const keys: string[] = [];
      const values: any[] = [];

      // 直接使用表单值，头像的fileId已经在上传时设置到表单中
      for (const param of this.systemConfig.params) {
        if (!param.disable) {
          keys.push(param.code);
          values.push(formValues[param.code]);
        }
      }

      const requestData = {
        bundleId: this.systemConfig.id,
        keys: keys,
        values: values
      };

      this.http
        .post(ConstantUtil.TENANT_CONFIG_V1_AILE_SAVE, requestData)
        .pipe(
          finalize(() => {
            this.configSaving = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('system-config.save.success'));
            // 清空临时数据
            this.tempAvatarFiles = {};
            this.avatarFileIds = {};
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('system-config.save.failed'));
          }
        });
    } catch (error) {
      this.configSaving = false;
      this.message.error(this.i18n.fanyi('system-config.save.failed'));
    }
  }
  ngOnInit(): void {
    this.loadSystemConfig();
  }

  loadSystemConfig(): void {
    this.configLoading = true;
    this.http
      .post(ConstantUtil.TENANT_CONFIG_V1_AILE_FORM, {})
      .pipe(
        finalize(() => {
          this.configLoading = false;
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.systemConfig = res.data;
            this.groupedConfigParams = this.groupConfigParams(res.data.params);
            this.initForm(res.data.params);
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('system-config.load.failed'));
        }
      });
  }

  private groupConfigParams(params: any[]): any[] {
    const groups = params.reduce((acc: any, curr: any) => {
      const group = acc.find((g: any) => g.groupName === curr.group);
      if (group) {
        group.params.push(curr);
      } else {
        acc.push({
          groupName: curr.group,
          params: [curr]
        });
      }
      return acc;
    }, []);

    return groups;
  }

  // 在initForm方法中添加URL验证
  private initForm(params: any[]): void {
    const group: any = {};
    params.forEach(param => {
      let validators = param.require ? [Validators.required] : [];

      // 为URL类型添加URL格式验证
      if (param.type === 'InputBox-Url') {
        validators = param.require ? [Validators.required, this.urlValidator] : [this.urlValidator];
      }

      group[param.code] = [
        {
          value: param.value,
          disabled: param.disable
        },
        validators
      ];

      // 如果是头像类型，加载头像图片
      if (param.type === 'InputBox-Avatar' && param.value) {
        // 避免重复加载
        if (!this.loadingAvatars.has(param.code)) {
          this.loadingAvatars.add(param.code);
          this.loadAvatarImage(param.code, param.value);
        }
      }
    });
    this.configForm = this.fb.group(group);
  }
  private urlValidator(control: any) {
    if (!control.value) {
      return null;
    }

    const urlPattern = /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    const valid = urlPattern.test(control.value);

    return valid ? null : { invalidUrl: true };
  }

  // 显示头像上传模态框
  showAvatarUploadModal(fieldCode: string): void {
    this.currentAvatarField = fieldCode;
    this.isAvatarModalVisible = true;
  }

  // 处理头像模态框取消
  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
    this.currentAvatarField = '';
  }

  // 加载头像图片
  private loadAvatarImage(fieldCode: string, avatarId: string): void {
    this.imageService.loadImage(avatarId, 'M').subscribe({
      next: imageUrl => {
        if (imageUrl) {
          this.avatarUrls[fieldCode] = imageUrl;
        }
        this.loadingAvatars.delete(fieldCode);
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingAvatars.delete(fieldCode);
        this.cdr.detectChanges();
      }
    });
  }
  resetConfig(): void {
    this.systemConfig.params.forEach((param: { code: string; value: any; defaultValue: any }) => {
      const control = this.configForm.get(param.code);
      if (control) {
        control.setValue(param.value !== null ? param.value : param.defaultValue);
        control.markAsPristine();
      }
    });
    this.message.info(this.i18n.fanyi('system-config.reset.success'));
  }

  getDictionaryOptions(dictionaryId: string): any[] {
    // 这里可以根据实际需求实现字典数据的获取
    return [];
  }
}
